// The planner's "Clinics" control: which clinics this device shows, and in what order the
// rows appear. Both are device-local preferences (workPlanHiddenClinicIds /
// workPlanClinicOrder) — nothing here changes the day for anyone else, which is why the
// copy says "on this device".
//
// Reordering is drag-the-grip, with ▲/▼ nudges alongside it for keyboard and trackpad-shy
// hands. The drag is plain pointer events on purpose: this popover renders inside the page's
// DndContext, whose PointerSensor owns dragging for ASSIGNMENT, and a dnd-kit draggable here
// would land in that same onDragEnd. Rows that are not dnd-kit draggables never reach its
// sensors, so the two drags stay independent.
import { useCallback, useRef, useState } from "react";
import { ArrowDownAZ, Check, ChevronDown, ChevronUp, GripVertical, MapPin, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { identityTone } from "./work-plan-utils";

/** The list as it looks mid-drag — same move semantics as moveClinicToIndex. */
function withMoved(list, from, to) {
  if (from === to || from < 0 || to < 0 || to >= list.length) return list;
  const next = [...list];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

function FooterButton({ onClick, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-3"
    >
      {children}
    </button>
  );
}

function NudgeButton({ onClick, disabled, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className="flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-25"
    >
      {children}
    </button>
  );
}

export default function ClinicListControl({
  // Already in the user's order — index 0 is the top row of the planner.
  clinics = [],
  hiddenIds,
  orderCustom = false,
  onToggle,
  onShowAll,
  onHideAll,
  onMove,
  onDropAt,
  onSortByName,
  onResetOrder,
}) {
  const listRef = useRef(null);
  // { id, from, to } while a grip is held; `to` is the slot the row would land in.
  const [drag, setDrag] = useState(null);

  const total = clinics.length;
  const hiddenCount = clinics.filter((clinic) => hiddenIds?.has(clinic.id)).length;
  const shownCount = total - hiddenCount;
  const isNarrowed = hiddenCount > 0;

  // Rows follow the pointer live, so the drop lands where the eye says it will.
  const displayed = drag ? withMoved(clinics, drag.from, drag.to) : clinics;

  const slotAtPoint = useCallback((clientY) => {
    const rows = Array.from(listRef.current?.querySelectorAll("[data-clinic-row]") || []);
    for (let index = 0; index < rows.length; index += 1) {
      const rect = rows[index].getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return index;
    }
    return Math.max(0, rows.length - 1);
  }, []);

  const handleGripDown = useCallback((event, index, clinicId) => {
    // Keep the press from starting a text selection / native drag of the popover content.
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ id: clinicId, from: index, to: index });
  }, []);

  const handleGripMove = useCallback(
    (event) => {
      if (!drag) return;
      const to = slotAtPoint(event.clientY);
      if (to !== drag.to) setDrag({ ...drag, to });
    },
    [drag, slotAtPoint],
  );

  // The release point decides the slot, not the last pointermove: a fast flick can end with
  // the pointer somewhere no move event ever reported. Rows already sit in the preview order
  // here, so the slot under the pointer IS the landing index.
  const endDrag = useCallback(
    (event) => {
      if (!drag) return;
      const to = Number.isFinite(event?.clientY) ? slotAtPoint(event.clientY) : drag.to;
      if (to !== drag.from) onDropAt?.(drag.id, to);
      setDrag(null);
    },
    [drag, onDropAt, slotAtPoint],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={total === 0}
          title="Choose which clinics this device shows, and their order"
          className={cn(
            "inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2 text-xs font-medium transition-colors",
            isNarrowed
              ? "border-primary/40 bg-primary/5 text-foreground"
              : "border-border bg-card text-foreground hover:bg-muted",
            total === 0 && "cursor-not-allowed opacity-50 hover:bg-card",
          )}
        >
          <MapPin className={cn("size-3.5", isNarrowed ? "text-primary" : "text-muted-foreground")} />
          <span>Clinics</span>
          <span aria-hidden className="mx-0.5 h-4 w-px bg-border" />
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
              isNarrowed ? "bg-primary/15 text-primary" : "bg-muted-foreground/10 text-muted-foreground",
            )}
          >
            {isNarrowed ? `${shownCount}/${total}` : "All"}
          </span>
          <ChevronDown className="size-3.5 text-muted-foreground/70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[20rem] p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-xs font-semibold">Clinics on this device</p>
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
            Untick to hide a clinic from the planner · drag the grip (or ▲▼) to reorder the rows.
          </p>
        </div>
        <ScrollArea className="max-h-[19rem]" viewportClassName="p-1.5">
          <div ref={listRef} className="flex flex-col">
            {displayed.map((clinic, index) => {
              const shown = !hiddenIds?.has(clinic.id);
              const dragging = drag?.id === clinic.id;
              return (
                <div
                  key={clinic.id}
                  data-clinic-row
                  className={cn(
                    "flex items-center gap-0.5 rounded-md pr-1 transition-colors",
                    dragging ? "bg-primary/10 ring-1 ring-primary/40" : "hover:bg-muted/60",
                  )}
                >
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Drag to reorder ${clinic.name}`}
                    title="Drag to reorder"
                    onPointerDown={(event) => handleGripDown(event, index, clinic.id)}
                    onPointerMove={handleGripMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    className={cn(
                      "flex size-5 shrink-0 touch-none items-center justify-center rounded text-muted-foreground/50 hover:text-foreground",
                      drag ? "cursor-grabbing" : "cursor-grab",
                    )}
                  >
                    <GripVertical className="size-3.5" />
                  </span>
                  <button
                    type="button"
                    onClick={() => onToggle?.(clinic.id)}
                    aria-pressed={shown}
                    title={shown ? `Hide ${clinic.name}` : `Show ${clinic.name}`}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left"
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                        shown
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/40",
                      )}
                    >
                      {shown ? <Check className="size-3" /> : null}
                    </span>
                    <span
                      className="size-2 shrink-0 rounded-full"
                      style={{ background: identityTone(clinic.id).solid }}
                    />
                    <span className={cn("truncate text-[12.5px]", !shown && "text-muted-foreground")}>
                      {clinic.name || clinic.shortName || clinic.id}
                    </span>
                  </button>
                  <NudgeButton
                    onClick={() => onMove?.(clinic.id, -1)}
                    disabled={index === 0 || Boolean(drag)}
                    title={`Move ${clinic.name} up`}
                  >
                    <ChevronUp className="size-3.5" />
                  </NudgeButton>
                  <NudgeButton
                    onClick={() => onMove?.(clinic.id, 1)}
                    disabled={index === total - 1 || Boolean(drag)}
                    title={`Move ${clinic.name} down`}
                  >
                    <ChevronDown className="size-3.5" />
                  </NudgeButton>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <div className="flex items-center justify-between gap-1 border-t border-border px-2 py-1.5">
          <div className="flex items-center gap-0.5">
            <FooterButton onClick={onShowAll} disabled={hiddenCount === 0} title="Show every clinic">
              Show all
            </FooterButton>
            <FooterButton onClick={onHideAll} disabled={shownCount === 0} title="Hide every clinic">
              Hide all
            </FooterButton>
          </div>
          <div className="flex items-center gap-0.5">
            <FooterButton onClick={onSortByName} title="Order the rows A–Z">
              <ArrowDownAZ />
              A–Z
            </FooterButton>
            <FooterButton
              onClick={onResetOrder}
              disabled={!orderCustom}
              title="Back to the default order"
            >
              <RotateCcw />
              Reset
            </FooterButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
